import { computed, Injectable, signal, Signal } from '@angular/core';
import { LoadingInfo } from '../types/loading-info.type';

const LOADING: LoadingInfo = {
  loading: false,
  message: $localize`Loading...`,
};

/**
 * Service to manage loading states across the application.
 * Supports both global loading (full-screen spinner) and granular operation tracking (skeleton loaders).
 *
 * @example
 * // Global loading spinner
 * loadingService.startLoading('Calculating prices...');
 * loadingService.stopLoading();
 *
 * @example
 * // Granular operation tracking
 * loadingService.setOperationLoading('extras', false);
 * loadingService.setOperationLoading('prices', false);
 * const loading = loadingService.areAllOperationsLoading(['extras', 'prices']);
 */
@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  /**
   * Private writable signal for managing loading state internally
   * @private
   */
  private readonly loadingState = signal<LoadingInfo>(LOADING);

  /**
   * Map to track individual operation loading states
   * @private
   */
  private readonly operationStates = new Map<string, ReturnType<typeof signal<boolean>>>();

  /**
   * Public readonly signal for global loading state.
   * Use this in templates or computed signals to react to loading changes.
   */
  readonly loading = this.loadingState.asReadonly();

  /**
   * Starts global loading with optional message
   * @param message - Optional loading message to display
   * @param operationKey - Optional operation key to track this loading state in granular tracking
   */
  startLoading(message?: string, operationKey?: string): void {
    this.setLoading(true, message, operationKey);
  }

  /**
   * Stops global loading
   * @param operationKey - Optional operation key to track this loading state in granular tracking
   */
  stopLoading(operationKey?: string): void {
    this.setLoading(false, '', operationKey);
  }

  /**
   * Creates a computed signal that returns true if all of the specified operations are loading
   * @param operationKeys - Array of operation identifiers to monitor
   * @returns Computed signal that is true only when all operations are loading
   */
  areAllOperationsLoading(operationKeys: string[]): ReturnType<typeof computed<boolean>> {
    return computed(() => {
      const states = operationKeys.map((key) => {
        const signal = this.getOperationLoadingSignal(key);
        return signal();
      });

      // All operations must be loading (true) for this to return true
      return states.every((state) => state === true);
    });
  }

  /**
   * Sets the loading state for a specific operation
   * @param operationKey - Unique identifier for the operation
   * @param loading - Whether the operation is loading or not
   */
  setOperationLoading(operationKey: string, loading: boolean): void {
    if (!this.operationStates.has(operationKey)) {
      this.operationStates.set(operationKey, signal<boolean>(loading));
    } else {
      const operationSignal = this.operationStates.get(operationKey);
      if (operationSignal) {
        operationSignal.set(loading);
      }
    }
  }

  /**
   * Public accessor to observe loading state for a specific operation
   * Returns a readonly Signal<boolean> so callers (components/templates) can react to changes.
   * @param operationKey - Unique identifier for the operation
   */
  isOperationLoading(operationKey: string): Signal<boolean> {
    return this.getOperationLoadingSignal(operationKey).asReadonly();
  }

  /**
   * Gets the loading signal for a specific operation
   * @param operationKey - Unique identifier for the operation
   * @returns Signal<boolean> indicating if the operation is loading
   */
  private getOperationLoadingSignal(operationKey: string): ReturnType<typeof signal<boolean>> {
    if (!this.operationStates.has(operationKey)) {
      this.operationStates.set(operationKey, signal<boolean>(false));
    }
    // We can safely use non-null assertion here because we just ensured the key exists above
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.operationStates.get(operationKey)!;
  }

  /**
   * Sets global loading state
   * @param loading - Whether to show the global loading spinner
   * @param message - Optional message to display with the loading spinner
   * @param operationKey - Optional operation key to track this loading state in granular tracking
   */
  private setLoading(loading: boolean, message?: string, operationKey?: string): void {
    // Update the signal with the new loading state
    this.loadingState.set({
      loading,
      message: message || LOADING.message,
    });

    if (operationKey) {
      this.setOperationLoading(operationKey, loading);
    }
  }
}
